import { Checkbox, CheckboxProps, Tooltip } from '@mui/material';
import { FC } from 'react';
import { HeaderCellProps } from '../types';

/**
 * Header checkbox that toggles selection across every selectable member in the
 * filtered list. Mosaic Support memberships are excluded from "selectable"
 * upstream, so this control never adds or removes the support account.
 *
 * Click behavior mirrors Gmail / Google Sheets: when anything is selected
 * (full or partial), click clears the selectable subset; when nothing is
 * selected, click selects every selectable member.
 */
export const TeamMemberCheckboxHeaderCell: FC<HeaderCellProps> = ({
  _customRowProps: {
    selectableMemberIds,
    selectedMemberIds,
    selectAllSelectableMembers,
    clearAllSelectableMembers,
  },
}) => {
  const selectableCount = selectableMemberIds.length;
  const selectedSelectableCount = selectableMemberIds.reduce(
    (count, id) => (selectedMemberIds.has(id) ? count + 1 : count),
    0,
  );

  const allChecked = selectableCount > 0 && selectedSelectableCount === selectableCount;
  const isIndeterminate = selectedSelectableCount > 0 && !allChecked;

  const handleClick: CheckboxProps['onClick'] = (event) => {
    event.stopPropagation();
    if (selectedSelectableCount > 0) {
      clearAllSelectableMembers();
    } else {
      selectAllSelectableMembers();
    }
  };

  const tooltip =
    selectedSelectableCount > 0
      ? 'Clear selection (excludes Mosaic Support)'
      : 'Select all members (excludes Mosaic Support)';

  return (
    <div>
      <Tooltip title={tooltip} placement="top">
        <span>
          <Checkbox
            checked={allChecked}
            indeterminate={isIndeterminate}
            onClick={handleClick}
            disabled={selectableCount === 0}
            inputProps={{
              'aria-label': tooltip,
            }}
          />
        </span>
      </Tooltip>
    </div>
  );
};
